import * as React from 'react';
import { useEffect, useState } from 'react';
import DropdownTreeSelect from 'react-dropdown-tree-select';
import 'react-dropdown-tree-select/dist/styles.css';
import { Button, Col, Row, Table, Toast, ToastBody, ToastHeader } from 'reactstrap';
import Swal from 'sweetalert2';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { ConceptNode, RestrictedAccountsFilter, loadRestrictedAccounts, selectAccountFilter, selectAccountTree } from '../../redux/AccountsSlice';
import { Afr, getSelectedAfr } from '../../redux/AfrSlice';
import { loadConcept, resetConceptState } from '../../redux/ConceptSlice';
import { Entity, getSelectedEntity } from '../../redux/EntitySlice';
import { AccountWithCode, ActionRequest, BypassStatus, MessageEvent, getMessages, requestBypass } from '../../redux/EventsSlice';
import { isAdmin } from '../../redux/MiscSlice';
import { TaxonomyTypeEnum, getTaxonomyReferenceForAfr } from '../../redux/TaxonomyVersionSlice';
import dateHelper from '../../utils/dateHelper';
import enums from '../../utils/enums';
import BypassStatusViewer from './BypassStatusViewer';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import utilityHelper from '../../utils/utilityHelper';


export interface SelectableListItem {
    label: string;
    value: string;
    disabled: boolean;
    checked: boolean;
    children: Array<SelectableListItem>;
}

const matchAccounts = function (a: AccountWithCode, b: AccountWithCode): boolean {
    if (a.account != b.account) {
        return false;
    }
    if (a.objectcode != b.objectcode) {
        return false;
    }
    return true;
}
const parseAccountReference = function (ref: string): AccountWithCode {
    var result = {
        account: ref,
        objectcode: ""
    }
    if (ref.length < 1) {
        return result;
    }
    if (ref < "5") {
        // this is a revenue or balance sheet account
        // TODO - How to handle impact fee codes
        return result;
    }
    if (ref.length < 5) {
        return result;
    }
    var idx = ref.indexOf(".");
    if (ref.length < 0) {
        // We missing the period on a balance sheet account
        result.account = ref.substring(0, 3);
        result.objectcode = ref.substring(3).replace("xx", "00");
    } else {
        result.account = ref.substring(0, idx);
        result.objectcode = ref.substring(idx + 1).replace("xx", "00");
    }
    return result;
}

const generateDisplayCode = function (account: AccountWithCode) {
    if (account.objectcode && account.objectcode.length > 0) {
        return account.account + "." + account.objectcode;
    } else {
        return account.account;
    }
}

const addToBypassSet = function (msg: MessageEvent, bypassSet: Array<BypassStatus>) {
    var account = msg.message.body;
    if (!account || account.length === 0) {
        var parts: string[] = msg.message.subject.split(": ");
        if (parts.length > 1) {
            account = parts[1];
        }
    }
    var msgAccount = parseAccountReference(account);
    var dcode = generateDisplayCode(msgAccount);
    var foundSet: Array<BypassStatus> = bypassSet.filter(bps => matchAccounts(bps.account, msgAccount));
    var matchedStatus: BypassStatus = {
        account: msgAccount,
        requestor: null,
        requested: null,
        response: null,
        status: enums.bypassStatus.pending,
        displayCode: dcode,
        reason: msg.payload
    };
    if (foundSet.length === 0) {
        bypassSet.push(matchedStatus);
    } else {
        matchedStatus = foundSet[0];
        matchedStatus.reason = msg.payload;
    }
    switch (msg.role) {
        case enums.bypassEventRoles.request:
            matchedStatus.requestor = msg.creator;
            matchedStatus.requested = msg.timestamp;
            matchedStatus.status = enums.bypassStatus.pending;
            break;

        case enums.bypassEventRoles.approve:
            matchedStatus.response = msg.timestamp;
            matchedStatus.status = enums.bypassStatus.approved;
            break;

        case enums.bypassEventRoles.reject:
            matchedStatus.response = msg.timestamp;
            matchedStatus.status = enums.bypassStatus.rejected;
            break;

        case enums.bypassEventRoles.withdraw:
            matchedStatus.response = msg.timestamp;
            matchedStatus.status = enums.bypassStatus.withdrawn;
            break;
    }
}

export const generateBypassSet = function (afrMessages: Array<MessageEvent>) : Array<BypassStatus> {
    var bypassSet: Array<BypassStatus> = [];
    if (afrMessages) {
        for (var msg of afrMessages) {
            if (msg) {
                switch (msg.role) {
                    case enums.bypassEventRoles.reject:
                    case enums.bypassEventRoles.approve:
                    case enums.bypassEventRoles.request:
                        addToBypassSet(msg, bypassSet);
                        break;

                    case enums.bypassEventRoles.withdraw:
                        addToBypassSet(msg, bypassSet);
                        break;
                }
            }
        }

    }
    return bypassSet;

}

const BypassRequest = (props: { userRole: string }) => {

    const dispatch = useAppDispatch();
    const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
    const afrYear = currentAfr ? currentAfr.year : 0;
    const entity: Entity | null = useAppSelector(getSelectedEntity);
    var ecode = entity ? entity.code : "";
    var ename = entity ? entity.name : "";
    var afrId = ecode + "," + afrYear;
    var afrMessages = useAppSelector(getMessages);

    const isAdminUser: boolean = useAppSelector(isAdmin);
    const accountTree = useAppSelector(selectAccountTree);
    const accountFilter = useAppSelector(selectAccountFilter);

    var events: Array<MessageEvent> = [];
    const [eventList, setEventList] = useState(events);

    const [selectedAccount, setSelectedAccount] = useState("");
    var bypasses: Array<BypassStatus> = [];
    const [bypassList, setBypassList] = useState(generateBypassSet(afrMessages));

    const [bypassByIndexIsOpen, setBypassByIndexIsOpen]
        = React.useState<{ isOpen: boolean; idx: number }>({ isOpen: false, idx: -1 });

    useEffect(function () {
        setBypassList(generateBypassSet(afrMessages));

    }, [afrMessages]);



    const buildSelectionTree = function (cnParent: ConceptNode | null, cnode: ConceptNode) {
        var disable = true;
        if (!cnode.children || cnode.children.length === 0) {
            disable = false;
        }
        var itemValue = cnode.concept.code;
        if (cnParent && (itemValue.length < 3)) {
            // This is an object code, so prepend with the account code
            itemValue = cnParent.concept.code + "." + cnode.concept.code;
        }
        var bChecked = (selectedAccount === itemValue);
        var listItem : SelectableListItem = {
            label: cnode.concept.name,
            value: itemValue,
            disabled: disable,
            checked: bChecked,
            children: []
        }
        if (cnode.children && cnode.children.length > 0) {
            for (var cnChild of cnode.children) {
                var bAddChild = true;
                var childCode = cnChild.concept.code;
                if (childCode.length < 3) {
                    // This is an object code child, so filter to restrictions
                    switch (cnode.concept.code) {
                        case "518":
                        case "581":
                            bAddChild = false; //(childCode !== "30");
                            break;

                        case "517":
                            bAddChild = false; //(childCode !== "70");
                            break;

                        case "587":
                            bAddChild = false; //(childCode !== "90");
                            break;
                    }
                }
                if (bAddChild) {
                    var childItem: SelectableListItem | null = buildSelectionTree(cnode, cnChild);
                    if (childItem) {
                        listItem.children.push(childItem);
                    }
                }
            }
            if (listItem.children.length === 0) {
                // all children have been filtered out so return null - this parent shouldn't be added as well
                return null;
            }
        }
        return listItem;
    }

    const getAccountsList = function () {
        var alist : Array<SelectableListItem> = [];
        if (!accountTree) {
            return alist;
        }
        if (accountTree.length > 0) {
            for (var root of accountTree) {
                var cnode = buildSelectionTree(null, root);
                if (cnode) {
                    alist.push(cnode);
                }
            }
        }
        return alist;
    }

    const ddhandleChange = function (currentNode : any, selectedNodes : any) {
        if (selectedNodes.length === 0) {
            setSelectedAccount("");
            dispatch(resetConceptState);
            return;
        }
        var conceptId = selectedNodes[0].value;
        setSelectedAccount(conceptId);
        if (conceptId.indexOf( "." ) === 3 && conceptId.length === 6) {
            conceptId = conceptId.substring(0, 3);
        }
        dispatch(loadConcept({
            conceptId: "AccountCode_" + conceptId,
            taxonomyType: currentAfr ? currentAfr.taxonomyType : TaxonomyTypeEnum.AFR,
            afrYear: currentAfr ? currentAfr.year : 2019
        }));
    }

    const handleBypassRequestClick = function() {
        var args: ActionRequest = {
            actionCode: selectedAccount,
            connector: afrId
        }
        dispatch(requestBypass(args))
            .then((result: any) => {
                if (result.payload != null) {
                    Swal.fire('Bypass Request Submitted', '', 'info');
                }
                else {
                    Swal.fire('Oops...', 'Something went wrong!', 'error')
                }
            });
        setSelectedAccount("");
        
    }

    const btnDisabled = (acct : string) : boolean => {
        return (acct.length === 0) ? true : false;
    }

    useEffect(() => {
        if (!accountFilter ||
            accountFilter.entityCode != ecode ||
            accountFilter.afrYear != afrYear) {
            //console.log("dispatching in use effect: " + (accountFilter ? JSON.stringify(accountFilter) : "no filter"));
            var accountQuery: RestrictedAccountsFilter = {
                entityCode: ecode,
                taxonomyVersion: getTaxonomyReferenceForAfr(currentAfr)
            }
            //console.log("new filter: " + JSON.stringify(accountQuery));
            dispatch(loadRestrictedAccounts(accountQuery));
        }
    }, []);

    return (
        <React.Fragment>
                <Row>
                    <Col sm="6">
                        <Row>
                            <h2>Account Bypass Request</h2>
                        <div>
                            <h5>
                                <React.Fragment>
                                    <br />
                                        <DropdownTreeSelect
                                            data={getAccountsList()}
                                            mode="radioSelect" keepTreeOnSearch={true}
                                            onChange={ddhandleChange}
                                            texts={{ placeholder: "Choose Account..." }} />
                                </React.Fragment>
                            </h5>
                            </div>
                        </Row>
                        <Row>
                            <Col className="d-flex justify-content-center pl-2 pr-3 py-3 my-2" >
                                <Button type="button" onClick={handleBypassRequestClick} disabled={btnDisabled(selectedAccount)}>Request Bypass</Button>
                            </Col>

                        </Row>
                        <Row>
                            <Col sm="12" >
                            <Toast className="auditDetail">
                                <ToastHeader className="auditDetail">All Account Bypass Requests</ToastHeader>
                                <ToastBody>
                                    <div className="scrollable-table">
                                        <Table className="table" >
                                            <thead className="scrollable-table-header thead-dark">
                                                <tr>
                                                    <th className="col-1">Account</th>
                                                    <th className="col-1">Object Code</th>
                                                    <th className="col-1">Requested</th>
                                                    <th className="col-1">Response</th>
                                                    <th className="col-1">Status</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {bypassList.map((option, idx) => (
                                                    <React.Fragment>
                                                        <tr
                                                            onClick={() => {
                                                                if (option.reason && option.reason.length > 0) {
                                                                    if (bypassByIndexIsOpen.idx == idx) {
                                                                        let toggleIsOpen: boolean = !bypassByIndexIsOpen.isOpen;
                                                                        let currentIdx: number = bypassByIndexIsOpen.idx;
                                                                        setBypassByIndexIsOpen({ isOpen: toggleIsOpen, idx: currentIdx })
                                                                    } else {
                                                                        setBypassByIndexIsOpen({ isOpen: true, idx: idx });
                                                                    }
                                                                }
                                                            }
                                                            }
                                                        >
                                                            <td>{option.account.account}</td>
                                                            <td>{option.account.objectcode}</td>
                                                            <td>{dateHelper.stringToDateString(option.requested)}</td>
                                                            <td>{dateHelper.stringToDateString(option.response)}</td>
                                                            <td>
                                                                {/*status*/}
                                                                {utilityHelper.SnakeCaseToTitleCase(option.status) + " "}

                                                                {/*collapse icon*/}
                                                                {
                                                                    option.reason && option.reason.length > 0
                                                                        && (bypassByIndexIsOpen.idx == idx && bypassByIndexIsOpen.isOpen == true) ?
                                                                        (
                                                                            <React.Fragment>
                                                                                <FontAwesomeIcon
                                                                                    style={{}} icon={faMinus} />
                                                                            </React.Fragment>
                                                                        ) : (<></>)
                                                                }

                                                                {/*expand icon*/}
                                                                {
                                                                    option.reason && option.reason.length > 0
                                                                        && (bypassByIndexIsOpen.idx != idx || bypassByIndexIsOpen.isOpen == false) ?
                                                                        (
                                                                            <React.Fragment>
                                                                                <FontAwesomeIcon
                                                                                    style={{}} icon={faPlus} />
                                                                            </React.Fragment>
                                                                        ) : (<></>)
                                                                }
                                                            </td>
                                                            
                                                        </tr>
                                                        {
                                                            option.reason && option.reason.length > 0 ?
                                                                (
                                                                    <tr hidden={bypassByIndexIsOpen.idx != idx || bypassByIndexIsOpen.isOpen == false }>
                                                                        <td colSpan={5}>
                                                                            {option.reason}
                                                                        </td>
                                                                    </tr>
                                                                ) : (<></>)

                                                        }
                                                    </React.Fragment>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </ToastBody>
                                </Toast>
                                </Col>
                        </Row>
                    </Col>
                    <Col sm="6">
                        <Row>
                            <BypassStatusViewer userRole={props.userRole} statusList={bypassList} status={enums.bypassStatus.pending} afrId={afrId} />
                        </Row>
                        <Row>
                            <BypassStatusViewer userRole={props.userRole} statusList={bypassList} status={enums.bypassStatus.approved} afrId={afrId} />
                        </Row>
                        <Row>
                            <BypassStatusViewer userRole={props.userRole} statusList={bypassList} status={enums.bypassStatus.rejected} afrId={afrId} />
                        </Row>
                        <Row>
                            <BypassStatusViewer userRole={props.userRole} statusList={bypassList} status={enums.bypassStatus.withdrawn} afrId={afrId} />
                        </Row>
                    </Col>
                </Row>
        </React.Fragment>
    );
}

export default BypassRequest;